import { useState } from 'react';

import {
  createColumnHelper,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';

import { formatDatetimeForTable } from '../../../lib/table-utils';
import { useFetchAllActiveRuns } from '@/api/queries/analyticsQueries/useFetchAllActiveRuns';
import { ActiveRun } from '@/api/types/backend-dto-types';
import { useGlobalAdminCheck } from '@/hooks/useGlobalAdminCheck';
import { Breadcrumb } from '@/new/components/breadcrumb';
import { PageContainer } from '@/new/components/page-container';
import { PageHeader } from '@/new/components/page-header';
import { PageTitle } from '@/new/components/page-title';
import { ScrollableTable } from '@/new/components/table/scrollable-table';

const fallbackTableData: ActiveRun[] = [];

const columnHelper = createColumnHelper<ActiveRun>();
const columns = [
  columnHelper.accessor('createdDate', {
    header: () => <span>Start Time</span>,
    cell: (info) => <span className="whitespace-nowrap">{formatDatetimeForTable(info.getValue())}</span>,
  }),
  columnHelper.accessor('completedAtEstimate', {
    header: () => <span>Est. Completion</span>,
    cell: (info) => {
      const value = info.getValue();
      return <span className="whitespace-nowrap">{value ? formatDatetimeForTable(value) : '—'}</span>;
    },
  }),
  columnHelper.accessor('matter.organisation.name', {
    header: () => <span>Organisation</span>,
    cell: (info) => <span>{info.getValue()}</span>,
  }),
  columnHelper.accessor('createdBy.email', {
    header: () => <span>User Email</span>,
    cell: (info) => <span>{info.getValue()}</span>,
  }),
  columnHelper.accessor('totalPages', {
    header: () => <span>Pages</span>,
    cell: (info) => <span>{info.getValue()}</span>,
  }),
  columnHelper.accessor('type', {
    header: () => <span>Type</span>,
    cell: (info) => <span className="capitalize">{info.getValue()}</span>,
  }),
  columnHelper.accessor('status', {
    header: () => <span>Status</span>,
    cell: (info) => <span className="capitalize">{info.getValue()}</span>,
  }),
];

export function AllRuns() {
  useGlobalAdminCheck();

  const { data: runsData } = useFetchAllActiveRuns();

  const [globalFilter, setGlobalFilter] = useState('');

  const table = useReactTable({
    data: runsData ?? fallbackTableData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    initialState: {
      sorting: [
        {
          id: 'createdDate',
          desc: true,
        },
      ],
      pagination: {
        pageSize: 15,
      },
    },
    state: {
      globalFilter,
    },
  });

  return (
    <>
      <Breadcrumb
        items={[
          { href: '/admin/organisations', label: 'Admin', active: false },
          { href: '/admin/runs', label: 'All Runs', active: true },
        ]}
      />
      <PageContainer>
        <PageHeader>
          <PageTitle text="All Runs" />
        </PageHeader>
      </PageContainer>

      <ScrollableTable table={table} plural="runs" globalFilter={globalFilter} setGlobalFilter={setGlobalFilter} />
    </>
  );
}
